// Единый источник правил клиентской валидации загружаемого чека.
//
// Правило: для всех мерчантов принимается PDF. Мерчанты `lion` и `chcmanual`
// дополнительно принимают изображения (JPEG/PNG/JPG). Лимит размера — 5 МБ для всех.
//
// Набор форматов ограничен тем, что реально принимает основной бэкенд платёжки
// (`InitMediaObjectRequest`: application/pdf, image/jpeg, image/png, image/jpg) —
// шире на клиенте разрешать нельзя, бэкенд отклонит.

import { normalizeMerchant } from './merchantName';
import { MAX_CONCIERGE_VIDEO_SIZE, VIDEO_EXTS, VIDEO_MIMES, isVideoFile } from './conciergeVideo';

export const MAX_RECEIPT_SIZE = 5 * 1024 * 1024; // 5 MB

// Мерчанты, которым помимо PDF разрешены изображения.
const IMAGE_ALLOWED_MERCHANTS = ['lion', 'chcmanual'];

type ReceiptPolicy = {
  // `types` для react-drag-drop-files FileUploader
  types: string[];
  // допустимые расширения (lowercase, без точки)
  exts: string[];
  // допустимые MIME-типы
  mimes: string[];
  // значение `accept` для нативного <input type="file">
  accept: string;
};

const PDF_ONLY: ReceiptPolicy = {
  types: ['PDF'],
  exts: ['pdf'],
  mimes: ['application/pdf'],
  accept: 'application/pdf,.pdf',
};

const PDF_AND_IMAGE: ReceiptPolicy = {
  types: ['PDF', 'JPEG', 'PNG', 'JPG'],
  exts: ['pdf', 'jpg', 'jpeg', 'png'],
  mimes: ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'],
  accept: 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png',
};

// The concierge chat: a PDF, any picture or a video, whatever the merchant's own receipt formats —
// the file reaches an operator, a picture that becomes the receipt is re-encoded by the backend, and
// a video never becomes the receipt.
const CONCIERGE: ReceiptPolicy = {
  types: ['PDF', 'JPEG', 'JPG', 'PNG', 'WEBP', 'HEIC', 'HEIF', 'GIF', 'BMP', 'MP4', 'MOV', 'M4V', 'WEBM', '3GP', '3GPP', 'MKV', 'AVI'],
  exts: ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif', 'bmp', ...VIDEO_EXTS],
  mimes: ['application/pdf'],
  accept: `application/pdf,image/*,${VIDEO_MIMES.join(',')},.pdf,.heic,.heif,${VIDEO_EXTS.map((ext) => `.${ext}`).join(',')}`,
};

export const allowsImageReceipt = (merchant?: string): boolean =>
  IMAGE_ALLOWED_MERCHANTS.includes(normalizeMerchant(merchant));

export const isReceiptPdfOnly = (merchant?: string): boolean =>
  !allowsImageReceipt(merchant);

const policyFor = (merchant?: string, concierge = false): ReceiptPolicy =>
  concierge ? CONCIERGE : allowsImageReceipt(merchant) ? PDF_AND_IMAGE : PDF_ONLY;

// Значение `types` для react-drag-drop-files FileUploader.
export const getReceiptUploaderTypes = (merchant?: string): string[] =>
  policyFor(merchant).types;

// Значение `accept` для нативного <input type="file">.
export const getReceiptInputAccept = (merchant?: string, concierge = false): string =>
  policyFor(merchant, concierge).accept;

export type ReceiptValidationError = 'invalidFormat' | 'fileTooLarge' | 'fileEmpty';

// CPAYMENT-1786: the message and the hint must follow the merchant's own policy.
// The one-page template hardcoded the "PDF or image" wording, so a PDF-only merchant told the client
// that images were fine and then rejected them — an instruction that contradicts itself is what
// produced the repeated attempts we saw in production.
export const getReceiptFormatErrorKey = (merchant?: string, concierge = false): string => {
  if (concierge) return 'upload.invalidFormatPdfImageOrVideo';
  return isReceiptPdfOnly(merchant) ? 'upload.invalidFormat' : 'upload.invalidFormatPdfOrImage';
};

export const getReceiptInstructionsKey = (merchant?: string): string =>
  isReceiptPdfOnly(merchant) ? 'upload.instructions' : 'upload.instructionsPdfOrImage';

// Проверка выбранного файла перед отправкой: тип (расширение или MIME) + размер.
export const validateReceiptFile = (
  merchant: string | undefined,
  file: File,
  concierge = false,
): { ok: boolean; error?: ReceiptValidationError } => {
  const { exts, mimes } = policyFor(merchant, concierge);
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const typeOk = concierge && file.type.startsWith('video/')
    ? isVideoFile(file)
    : mimes.includes(file.type) || exts.includes(ext) || (concierge && (file.type.startsWith('image/') || isVideoFile(file)));
  if (!typeOk) {
    return { ok: false, error: 'invalidFormat' };
  }
  // A zero-length pick passes both the format and the ceiling check, so before this it went out as
  // an empty multipart and came back a bare 400 the client could only read as "upload failed" —
  // and repeat, because nothing told him the file itself was the problem. Measured on prod: one
  // deposit expired after 22 such attempts in nine minutes. The picker produces these on its own
  // (a cloud file not yet downloaded, a provider handing back an empty stream); the fix is to name
  // it here, where the client can still pick another file.
  if (file.size === 0) {
    return { ok: false, error: 'fileEmpty' };
  }
  const ceiling = concierge && isVideoFile(file) ? MAX_CONCIERGE_VIDEO_SIZE : MAX_RECEIPT_SIZE;
  if (file.size > ceiling) {
    return { ok: false, error: 'fileTooLarge' };
  }
  return { ok: true };
};
